/**
 * @fileoverview DashboardHeader's command-palette-style page search.
 * @description Ported from the InsightBox Banking design system's own `GlobalSearch` — a
 * type-to-filter box over every nav page the signed-in user can reach (`NAV_ITEMS`, the same
 * privilege-filtered list NavMenu renders), with arrow-key navigation, Enter to go, Escape to
 * close, and a global "/" shortcut that focuses the box from anywhere on the page (skipped while
 * focus is already inside another text field). Lives in `shared/` since `NAV_ITEMS` is a shared
 * constant, not a feature — no ADR-0002 boundary issue building the index straight from it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { NAV_ITEMS } from '@/shared/constants/nav-items.constants'
import { useOutsideClick } from '@/shared/hooks/useOutsideClick'

interface DashboardHeaderSearchProps {
  privileges: string[]
}

/**
 * @component
 * @param {DashboardHeaderSearchProps} props - The signed-in user's privileges, used to build the
 * search index from only the nav pages they can actually reach.
 * @param {string[]} props.privileges - Privilege keys from GET /identity/me.
 */
function DashboardHeaderSearch({ privileges }: DashboardHeaderSearchProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const close = () => setOpen(false)
  const containerRef = useOutsideClick<HTMLDivElement>(open, close)

  const index = useMemo(
    () => NAV_ITEMS.filter((item) => privileges.includes(item.privilege)),
    [privileges],
  )

  const results = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) {
      return []
    }
    return index
      .filter((item) => t(item.labelKey).toLowerCase().includes(normalizedQuery))
      .slice(0, 8)
  }, [index, query, t])

  useEffect(() => {
    /**
     * @description Focuses the search box on "/", unless focus is already inside a text field.
     * @param {KeyboardEvent} event - The document keydown event.
     */
    function handleGlobalKeyDown(event: KeyboardEvent) {
      const focusedTag = document.activeElement?.tagName
      if (event.key !== '/' || focusedTag === 'INPUT' || focusedTag === 'TEXTAREA') {
        return
      }
      event.preventDefault()
      inputRef.current?.focus()
    }
    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  /**
   * @description Navigates to a search result and resets the box.
   * @param {(typeof NAV_ITEMS)[number]} entry - The chosen nav item.
   */
  function goTo(entry: (typeof NAV_ITEMS)[number]) {
    void navigate(entry.path)
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
  }

  /**
   * @description Handles arrow/Enter/Escape while the result list is open.
   * @param {React.KeyboardEvent} event - The search input's keydown event.
   */
  function handleKeyDown(event: React.KeyboardEvent) {
    if (!open || results.length === 0) {
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const entry = results[activeIndex]
      if (entry) {
        goTo(entry)
      }
    } else if (event.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div className="relative w-full max-w-xs" ref={containerRef}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-placeholder"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
          setActiveIndex(0)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={t('layout.header.searchPlaceholder')}
        className="w-full rounded-lg border border-input-border bg-input-bg py-2 pl-9 pr-8 text-sm text-ink outline-none transition placeholder:text-placeholder focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20"
      />
      {query && (
        <button
          type="button"
          onClick={() => {
            setQuery('')
            inputRef.current?.focus()
          }}
          aria-label={t('layout.header.clearSearch')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer text-placeholder transition hover:text-slate"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
          </svg>
        </button>
      )}

      {open && query && (
        <div className="absolute left-0 right-0 z-50 mt-1.5 max-h-80 overflow-y-auto rounded-xl border border-divider bg-white shadow-card">
          {results.length === 0 ? (
            <p className="px-4 py-3 text-sm text-slate">
              {t('layout.header.noSearchMatches', { query })}
            </p>
          ) : (
            results.map((entry, index) => (
              <button
                key={entry.path}
                type="button"
                onClick={() => goTo(entry)}
                onMouseEnter={() => setActiveIndex(index)}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition ${
                  index === activeIndex ? 'bg-primary-light/60' : 'hover:bg-input-bg'
                }`}
              >
                <span className="truncate text-sm font-medium text-ink">{t(entry.labelKey)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default DashboardHeaderSearch
